import { useState } from "react";
import type { ColorSwatch } from "@onetasklist/shared/colors";

// Matches Fluent UI's HsvColor: h 0-360, s 0-1, v 0-1
export type HsvColor = { h: number; s: number; v: number };

const VALID_HEX = /^[0-9a-fA-F]{6}$/;

export function hexToHsv(hex: string): HsvColor {
  const h6 = hex.replace("#", "").slice(0, 6);
  const r = parseInt(h6.slice(0, 2), 16) / 255;
  const g = parseInt(h6.slice(2, 4), 16) / 255;
  const b = parseInt(h6.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  if (delta > 0) {
    if (max === r) h = ((g - b) / delta) % 6;
    else if (max === g) h = (b - r) / delta + 2;
    else h = (r - g) / delta + 4;
    h = Math.round(h * 60);
    if (h < 0) h += 360;
  }
  return {
    h,
    s: max === 0 ? 0 : delta / max,
    v: max,
  };
}

export function hsvToHex({ h, s, v }: HsvColor): string {
  const f = (n: number) => {
    const k = (n + h / 60) % 6;
    return v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
  };
  const hex2 = (x: number) => Math.round(x * 255).toString(16).padStart(2, "0");
  return `#${hex2(f(5))}${hex2(f(3))}${hex2(f(1))}`;
}

export interface ColorPickerViewModel {
  showCustom: boolean;
  customHsv: HsvColor;
  hexInput: string;
  isCustomSelected: boolean;
  openCustom: () => void;
  closeCustom: () => void;
  handleCustomColorChange: (color: HsvColor) => void;
  handleHexInputChange: (value: string) => void;
  handleSwatchSelect: (value: string) => void;
  handleUnsetSelect: () => void;
}

export interface ColorPickerViewModelProps {
  colors: ColorSwatch[];
  selected?: string;
  onSelect: (value: string) => void;
}

export function useColorPickerViewModel({
  colors,
  selected,
  onSelect,
}: ColorPickerViewModelProps): ColorPickerViewModel {
  const [showCustom, setShowCustom] = useState(false);
  const [customHsv, setCustomHsv] = useState<HsvColor>({ h: 0, s: 1, v: 1 });
  const [hexInput, setHexInput] = useState("ff0000");

  const isCustomSelected =
    !!selected &&
    !colors.some((c) => c.value?.toLowerCase() === selected.toLowerCase());

  function openCustom() {
    const startHex = selected?.match(/^#[0-9a-f]{6}$/i) ? selected : "#ff0000";
    setCustomHsv(hexToHsv(startHex));
    setHexInput(startHex.replace("#", "").toLowerCase());
    setShowCustom(true);
  }

  function closeCustom() {
    setShowCustom(false);
  }

  function handleCustomColorChange(color: HsvColor) {
    setCustomHsv(color);
    const hex = hsvToHex(color);
    setHexInput(hex.replace("#", ""));
    onSelect(hex);
  }

  function handleHexInputChange(value: string) {
    setHexInput(value);
    if (VALID_HEX.test(value)) {
      const hex = `#${value.toLowerCase()}`;
      setCustomHsv(hexToHsv(hex));
      onSelect(hex);
    }
  }

  function handleSwatchSelect(value: string) {
    setShowCustom(false);
    onSelect(value);
  }

  function handleUnsetSelect() {
    setShowCustom(false);
    onSelect("");
  }

  return {
    showCustom,
    customHsv,
    hexInput,
    isCustomSelected,
    openCustom,
    closeCustom,
    handleCustomColorChange,
    handleHexInputChange,
    handleSwatchSelect,
    handleUnsetSelect,
  };
}
